import React, { useState } from 'react'
import { Box, TextField, Button, Typography, Fade, Card, CardContent, Avatar, Zoom } from "@mui/material"
import { styled, keyframes } from "@mui/material/styles"
import * as actions from "../common/actions"
import { toastError, toastSuccess, validateEmail } from '../../utils';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import SendIcon from '@mui/icons-material/Send';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CelebrationIcon from '@mui/icons-material/Celebration';
import GroupsIcon from '@mui/icons-material/Groups';
import WavingHandIcon from '@mui/icons-material/WavingHand';
import SEO from '../../components/SEO';

const float = keyframes`
    0%, 100% {
        transform: translateY(0px) rotate(0deg);
    }
    50% {
        transform: translateY(-20px) rotate(5deg);
    }
`;

const pulse = keyframes`
    0%, 100% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.1);
    }
`;

const wave = keyframes`
    0%, 100% {
        transform: rotate(0deg);
    }
    25% {
        transform: rotate(20deg);
    }
    75% {
        transform: rotate(-20deg);
    }
`;

const PageContainer = styled(Box)({
    minHeight: 'calc(100vh - 64px)',
    background: 'linear-gradient(135deg, #e8eef3 0%, #dce4ec 100%)',
    padding: '32px 24px',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        width: '300px',
        height: '300px',
        background: 'radial-gradient(circle, rgba(26, 115, 232, 0.08) 0%, transparent 70%)',
        borderRadius: '50%',
        top: '-100px',
        right: '-100px',
        animation: `${pulse} 4s ease-in-out infinite`,
        pointerEvents: 'none',
    },
    '&::after': {
        content: '""',
        position: 'absolute',
        width: '400px',
        height: '400px',
        background: 'radial-gradient(circle, rgba(0, 191, 165, 0.06) 0%, transparent 70%)',
        borderRadius: '50%',
        bottom: '-150px',
        left: '-150px',
        animation: `${pulse} 5s ease-in-out infinite`,
        pointerEvents: 'none',
    },
});

const FloatingIcon = styled(Box)(({ delay = 0 }) => ({
    position: 'absolute',
    animation: `${float} 6s ease-in-out infinite`,
    animationDelay: `${delay}s`,
    opacity: 0.15,
    zIndex: 0,
}));

const PageHeader = styled(Box)({
    marginBottom: '24px',
    textAlign: 'center',
    '& h1': {
        fontSize: '1.75rem',
        fontWeight: 700,
        background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        marginBottom: '8px',
        fontFamily: "'DM Sans', sans-serif",
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
    },
    '& p': {
        fontSize: '0.9rem',
        color: '#5f6368',
        fontFamily: "'DM Sans', sans-serif",
    },
});

const StyledCard = styled(Card)({
    borderRadius: '20px',
    overflow: 'visible',
    boxShadow: '0 8px 32px rgba(26, 115, 232, 0.15)',
    background: 'white',
    maxWidth: '550px',
    width: '100%',
    maxHeight: '90vh',
    transition: 'all 0.3s ease',
    position: 'relative',
    zIndex: 1,
    '&:hover': {
        boxShadow: '0 12px 48px rgba(26, 115, 232, 0.25)',
        transform: 'translateY(-4px)',
    },
});

const IconAvatar = styled(Avatar)({
    width: '70px',
    height: '70px',
    background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
    boxShadow: '0 8px 24px rgba(26, 115, 232, 0.3)',
    margin: '0 auto 12px',
    animation: `${pulse} 3s ease-in-out infinite`,
    '& svg': {
        fontSize: '36px',
    },
});

const StyledTextField = styled(TextField)({
    fontFamily: "'DM Sans', sans-serif",
    width: "100%",
    '& .MuiInputLabel-root': {
        fontFamily: "'DM Sans', sans-serif",
        color: '#5f6368',
        fontSize: '0.95rem',
        fontWeight: 500,
    },
    '& .MuiInputLabel-root.Mui-focused': {
        color: '#1a73e8',
        fontWeight: 600,
    },
    '& .MuiOutlinedInput-root': {
        backgroundColor: '#f8f9fa',
        borderRadius: '12px',
        fontFamily: "'DM Sans', sans-serif",
        transition: 'all 0.3s ease',
        '& fieldset': {
            borderColor: 'rgba(0, 0, 0, 0.12)',
            borderWidth: '2px',
        },
        '&:hover fieldset': {
            borderColor: 'rgba(26, 115, 232, 0.5)',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#1a73e8',
            borderWidth: '2px',
        },
        '&.Mui-focused': {
            backgroundColor: 'white',
        },
    },
    '& .MuiOutlinedInput-input': {
        padding: '16px 14px',
        fontSize: '1rem',
        color: '#202124',
        fontWeight: 500,
    },
});

const StyledSubmit = styled(Button)({
    fontFamily: "'DM Sans', sans-serif",
    background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
    fontSize: '1rem',
    height: '50px',
    width: '100%',
    borderRadius: '12px',
    color: "white",
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: '0 4px 12px rgba(26, 115, 232, 0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
        background: 'linear-gradient(135deg, #1557b0 0%, #009688 100%)',
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 16px rgba(26, 115, 232, 0.4)',
    },
});

function Invite(props) {

    const [invite, setInvite] = useState({
        email: '',
        name: ''
    })

    const onSubmitSendEmail = () => {
        if (!validateEmail(invite.email)) return toastError('Invalid Email.')
        if (!invite.name) return toastError('Name is required.')
        props.setLoader(true)
        actions.sendInvite(invite).then(res => {
            props.setLoader(false)
            toastSuccess(res.message)
            setInvite({ email: '', name: '' })
        }).catch(err => {
            props.setLoader(false)
            toastError(err.message)
        })
    }

    return (
        <PageContainer>
            <SEO
                title="Invite User"
                description="Send an invitation to new users to join Fronic Media music distribution platform."
            />
            {/* Floating Decorative Icons */}
            <FloatingIcon sx={{ top: '10%', left: '10%' }} delay={0}>
                <RocketLaunchIcon sx={{ fontSize: 60, color: '#1a73e8' }} />
            </FloatingIcon>
            <FloatingIcon sx={{ top: '20%', right: '15%' }} delay={1}>
                <CelebrationIcon sx={{ fontSize: 70, color: '#00bfa5' }} />
            </FloatingIcon>
            <FloatingIcon sx={{ bottom: '15%', left: '8%' }} delay={2}>
                <GroupsIcon sx={{ fontSize: 80, color: '#667eea' }} />
            </FloatingIcon>
            <FloatingIcon sx={{ bottom: '25%', right: '10%' }} delay={1.5}>
                <WavingHandIcon sx={{ fontSize: 65, color: '#ff9800', animation: `${wave} 2s ease-in-out infinite` }} />
            </FloatingIcon>

            <Fade in timeout={800}>
                <StyledCard>
                    <CardContent sx={{ p: 3, overflowY: 'auto', maxHeight: '90vh' }}>
                        {/* Animated Avatar Icon */}
                        <Zoom in timeout={1000}>
                            <IconAvatar>
                                <PersonAddIcon />
                            </IconAvatar>
                        </Zoom>

                        {/* Header */}
                        <PageHeader>
                            <Typography variant="h1">
                                Invite User
                            </Typography>
                            <Typography>
                                Send an invitation to a new user to join Fronic Media
                            </Typography>
                        </PageHeader>

                        {/* Form */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Name Field */}
                            <Zoom in timeout={1200}>
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Box
                                            sx={{
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                borderRadius: '8px',
                                                width: '32px',
                                                height: '32px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                boxShadow: '0 2px 8px rgba(102, 126, 234, 0.25)',
                                            }}
                                        >
                                            <PersonIcon sx={{ fontSize: 18, color: 'white' }} />
                                        </Box>
                                        <Typography
                                            sx={{
                                                fontFamily: "'DM Sans', sans-serif",
                                                fontWeight: 600,
                                                color: '#202124',
                                                fontSize: '0.95rem'
                                            }}
                                        >
                                            Full Name
                                        </Typography>
                                    </Box>
                                    <StyledTextField
                                        placeholder="John Doe"
                                        variant="outlined"
                                        value={invite.name}
                                        onChange={(e) => setInvite({ ...invite, name: e.target.value })}
                                        fullWidth
                                    />
                                </Box>
                            </Zoom>

                            {/* Email Field */}
                            <Zoom in timeout={1400}>
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Box
                                            sx={{
                                                background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
                                                borderRadius: '8px',
                                                width: '32px',
                                                height: '32px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                boxShadow: '0 2px 8px rgba(26, 115, 232, 0.25)',
                                            }}
                                        >
                                            <EmailIcon sx={{ fontSize: 18, color: 'white' }} />
                                        </Box>
                                        <Typography
                                            sx={{
                                                fontFamily: "'DM Sans', sans-serif",
                                                fontWeight: 600,
                                                color: '#202124',
                                                fontSize: '0.95rem'
                                            }}
                                        >
                                            Email Address
                                        </Typography>
                                    </Box>
                                    <StyledTextField
                                        placeholder="example@email.com"
                                        variant="outlined"
                                        value={invite.email}
                                        onChange={(e) => setInvite({ ...invite, email: e.target.value })}
                                        fullWidth
                                        type="email"
                                    />
                                </Box>
                            </Zoom>

                            {/* Submit Button */}
                            <Zoom in timeout={1600}>
                                <Box sx={{ mt: 1 }}>
                                    <StyledSubmit
                                        onClick={onSubmitSendEmail}
                                        startIcon={<RocketLaunchIcon sx={{ animation: `${pulse} 2s ease-in-out infinite` }} />}
                                        endIcon={<SendIcon />}
                                    >
                                        Send Invitation
                                    </StyledSubmit>
                                </Box>
                            </Zoom>
                        </Box>

                        {/* Fun Stats/Features */}
                        <Zoom in timeout={1800}>
                            <Box
                                sx={{
                                    mt: 2.5,
                                    display: 'flex',
                                    justifyContent: 'space-around',
                                    flexWrap: 'wrap',
                                    gap: 1.5,
                                }}
                            >
                                <Box sx={{ textAlign: 'center' }}>
                                    <Box
                                        sx={{
                                            width: '42px',
                                            height: '42px',
                                            background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                                            borderRadius: '10px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            margin: '0 auto 6px',
                                            animation: `${pulse} 3s ease-in-out infinite`,
                                            animationDelay: '0.2s',
                                        }}
                                    >
                                        <RocketLaunchIcon sx={{ color: 'white', fontSize: 22 }} />
                                    </Box>
                                    <Typography sx={{ fontSize: '0.7rem', color: '#5f6368', fontWeight: 600 }}>
                                        Instant
                                    </Typography>
                                </Box>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Box
                                        sx={{
                                            width: '42px',
                                            height: '42px',
                                            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                                            borderRadius: '10px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            margin: '0 auto 6px',
                                            animation: `${pulse} 3s ease-in-out infinite`,
                                            animationDelay: '0.4s',
                                        }}
                                    >
                                        <EmailIcon sx={{ color: 'white', fontSize: 22 }} />
                                    </Box>
                                    <Typography sx={{ fontSize: '0.7rem', color: '#5f6368', fontWeight: 600 }}>
                                        Email Sent
                                    </Typography>
                                </Box>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Box
                                        sx={{
                                            width: '42px',
                                            height: '42px',
                                            background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                                            borderRadius: '10px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            margin: '0 auto 6px',
                                            animation: `${pulse} 3s ease-in-out infinite`,
                                            animationDelay: '0.6s',
                                        }}
                                    >
                                        <GroupsIcon sx={{ color: 'white', fontSize: 22 }} />
                                    </Box>
                                    <Typography sx={{ fontSize: '0.7rem', color: '#5f6368', fontWeight: 600 }}>
                                        Easy Setup
                                    </Typography>
                                </Box>
                            </Box>
                        </Zoom>
                    </CardContent>
                </StyledCard>
            </Fade>
        </PageContainer>
    )
}

export default Invite
